import {
  Timestamp,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type Firestore,
  type QueryDocumentSnapshot,
  type Unsubscribe,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { firebaseFunctions, firestoreDb } from './firebase'
import {
  type PendingInvite,
  type PendingInviteRole,
  type PendingInviteStatus,
  type PendingInviteType,
  type WebPendingInvite,
  type WebPendingInviteRole,
  isWebPendingInvite,
} from '../types/invites'
import type { WorkspaceRole } from '../types/workspace'
import type { AccessScope } from '../types/schema'
import { isWorkspaceRole } from '../utils/workspacePermissions'
import { isNoSignInPreviewActive } from '../debug'
import { getMaxNonOwnedWorkspaces } from '../utils/workspaceLimits'

/**
 * Debug-bypass in-memory invite store. When localStorage.noSignIn === 'debug',
 * the user has no Firebase Auth token and every Firestore write to
 * pending_invites is rejected by rules. To exercise the InvitesHub flow
 * end-to-end without touching Firestore, every service function below checks
 * isDebugBypassActive() first and either reads/writes this in-memory map or
 * falls through to the real Firestore implementation.
 *
 * The store is pinned onto `window.__DEBUG_INVITE_STORE__` so all module
 * instances (Vite HMR can create more than one) share the same Map.
 */
const DEBUG_STORE_GLOBAL_KEY = '__DEBUG_INVITE_STORE__'
const DEBUG_STORE_STORAGE_KEY = 'timelining.debug.pendingInvites'
type SerializedDebugInvite = Omit<PendingInvite, 'createdAt' | 'claimedAt' | 'revokedAt' | 'declinedAt'> & {
  declinedAtMs: number | null
  createdAtMs: number | null
  claimedAtMs: number | null
  revokedAtMs: number | null
}

const serializeDebugInvite = (invite: PendingInvite): SerializedDebugInvite => ({
  ...invite,
  declinedAtMs: invite.declinedAt?.toMillis?.() ?? null,
  createdAtMs: invite.createdAt?.toMillis?.() ?? null,
  claimedAtMs: invite.claimedAt?.toMillis?.() ?? null,
  revokedAtMs: invite.revokedAt?.toMillis?.() ?? null,
})

const deserializeDebugInvite = (invite: SerializedDebugInvite): PendingInvite => ({
  ...invite,
  declinedAt: typeof invite.declinedAtMs === 'number' ? Timestamp.fromMillis(invite.declinedAtMs) : null,
  createdAt: typeof invite.createdAtMs === 'number' ? Timestamp.fromMillis(invite.createdAtMs) : null,
  claimedAt: typeof invite.claimedAtMs === 'number' ? Timestamp.fromMillis(invite.claimedAtMs) : null,
  revokedAt: typeof invite.revokedAtMs === 'number' ? Timestamp.fromMillis(invite.revokedAtMs) : null,
})

const persistDebugInviteStore = (store: Map<string, PendingInvite>): void => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(
      DEBUG_STORE_STORAGE_KEY,
      JSON.stringify(Array.from(store.values()).map(serializeDebugInvite)),
    )
  } catch {
    // localStorage unavailable — in-memory debug store still works for current page.
  }
}

const getDebugInviteStore = (): Map<string, PendingInvite> => {
  const globalObj = typeof window !== 'undefined'
    ? (window as unknown as Record<string, unknown>)
    : (globalThis as unknown as Record<string, unknown>)
  const existing = globalObj[DEBUG_STORE_GLOBAL_KEY]
  if (existing instanceof Map) return existing as Map<string, PendingInvite>
  const created = new Map<string, PendingInvite>()
  if (typeof window !== 'undefined') {
    try {
      const raw = window.localStorage?.getItem(DEBUG_STORE_STORAGE_KEY)
      const parsed = raw ? JSON.parse(raw) as SerializedDebugInvite[] : []
      parsed.forEach((entry) => {
        const invite = deserializeDebugInvite(entry)
        created.set(invite.id, invite)
      })
    } catch {
      // Ignore stale or malformed debug payloads.
    }
  }
  globalObj[DEBUG_STORE_GLOBAL_KEY] = created
  return created
}
const debugInviteStore = getDebugInviteStore()

const debugTimestamp = (): Timestamp => Timestamp.fromMillis(Date.now())

const debugInsertInvite = (input: Omit<PendingInvite, 'createdAt'>): PendingInvite => {
  const invite: PendingInvite = { ...input, createdAt: debugTimestamp() }
  debugInviteStore.set(invite.id, invite)
  persistDebugInviteStore(debugInviteStore)
  return invite
}

const debugListForWorkspace = (workspaceId: string): PendingInvite[] =>
  Array.from(debugInviteStore.values()).filter(
    (entry) => entry.workspaceId === workspaceId && entry.status === 'PENDING',
  )

const debugListForRecipient = (type: 'EMAIL' | 'PHONE', recipient: string): PendingInvite[] =>
  Array.from(debugInviteStore.values()).filter(
    (entry) => entry.type === type && entry.recipient === recipient,
  )

const COLLECTION = 'pending_invites'
const INCOMING_INVITE_STATUSES: PendingInviteStatus[] = ['PENDING', 'CLAIMED', 'DECLINED']

const normalizeEmail = (email: string) => email.trim().toLowerCase()

const generateInviteToken = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `inv_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
}

const inviteRef = (db: Firestore, inviteId: string) => doc(db, COLLECTION, inviteId)

const isTimestamp = (val: unknown): val is Timestamp =>
  Boolean(
    val &&
    typeof val === 'object' &&
    typeof (val as { toMillis?: unknown }).toMillis === 'function',
  )

const mapInvite = (snapshot: QueryDocumentSnapshot): PendingInvite => {
  const data = snapshot.data() as Record<string, unknown>
  return {
    id: snapshot.id,
    type: (data.type as PendingInviteType) ?? 'EMAIL',
    recipient: typeof data.recipient === 'string' ? data.recipient : '',
    token: typeof data.token === 'string' ? data.token : snapshot.id,
    role: (data.role as WorkspaceRole) ?? 'HELPER',
    workspaceId: typeof data.workspaceId === 'string' ? data.workspaceId : '',
    workspaceName: typeof data.workspaceName === 'string' ? data.workspaceName : '',
    workspaceSlug: typeof data.workspaceSlug === 'string' ? data.workspaceSlug : null,
    status: (data.status as PendingInviteStatus) ?? 'PENDING',
    claimedBy: typeof data.claimedBy === 'string' ? data.claimedBy : null,
    invitedBy: typeof data.invitedBy === 'string' ? data.invitedBy : '',
    invitedByName: typeof data.invitedByName === 'string' ? data.invitedByName : null,
    notifiedUserId: typeof data.notifiedUserId === 'string' ? data.notifiedUserId : null,
    createdAt: isTimestamp(data.createdAt) ? (data.createdAt as Timestamp) : null,
    claimedAt: isTimestamp(data.claimedAt) ? (data.claimedAt as Timestamp) : null,
    revokedAt: isTimestamp(data.revokedAt) ? (data.revokedAt as Timestamp) : null,
    declinedAt: isTimestamp(data.declinedAt) ? (data.declinedAt as Timestamp) : null,
    expiresAt: typeof data.expiresAt === 'number' ? data.expiresAt : null,
    migratedFromMembershipId: typeof data.migratedFromMembershipId === 'string' ? data.migratedFromMembershipId : null,
    inviteStatusHistory: Array.isArray(data.inviteStatusHistory) ? data.inviteStatusHistory : undefined,
    ...(data.accessScope ? { accessScope: data.accessScope as AccessScope } : {}),
  }
}

export const INVITE_TTL_DAYS = 14
export const INVITE_TTL_MS = INVITE_TTL_DAYS * 24 * 60 * 60 * 1000

export const isInviteExpired = (invite: { status: string; expiresAt?: number | null }): boolean =>
  invite.status === 'EXPIRED' || (typeof invite.expiresAt === 'number' && invite.expiresAt > 0 && invite.expiresAt <= Date.now())

const shouldIncludeAccessScope = (
  role: PendingInviteRole,
  scope?: AccessScope,
): boolean => {
  return (
    (role === 'EDITOR' || role === 'VIEWER' || role === 'READONLY') &&
    scope?.mode === 'RESTRICTED'
  )
}

export interface CreateInviteInput {
  workspaceId: string
  workspaceName: string
  workspaceSlug?: string | null
  role: PendingInviteRole
  invitedBy: string
  invitedByName?: string | null
  expiresAtMs?: number | null
  accessScope?: AccessScope
}

/** Create an EMAIL-type pending invite. The doc id IS the token. */
export const createEmailInvite = async (
  input: CreateInviteInput & { email: string },
): Promise<PendingInvite & { resent?: boolean }> => {
  const recipient = normalizeEmail(input.email)
  if (!recipient) throw new Error('Email is required for an email invite.')
  const expiresAt = input.expiresAtMs ?? (Date.now() + INVITE_TTL_MS)

  if (isNoSignInPreviewActive()) {
    const existing = Array.from(debugInviteStore.values()).find(
      (entry) => entry.workspaceId === input.workspaceId && entry.type === 'EMAIL' && entry.recipient === recipient && entry.status !== 'CLAIMED',
    )
    if (existing) {
      const history = Array.isArray(existing.inviteStatusHistory) ? [...existing.inviteStatusHistory] : []
      history.push({ status: 'PENDING', changedAt: Date.now(), reason: 'RESENT' })
      const updated: PendingInvite = {
        ...existing,
        role: input.role,
        workspaceName: input.workspaceName,
        workspaceSlug: input.workspaceSlug ?? null,
        invitedBy: input.invitedBy,
        invitedByName: input.invitedByName ?? null,
        status: 'PENDING',
        createdAt: debugTimestamp(),
        expiresAt,
        claimedAt: null,
        revokedAt: null,
        declinedAt: null,
        inviteStatusHistory: history,
        ...(shouldIncludeAccessScope(input.role, input.accessScope) && input.accessScope
          ? { accessScope: input.accessScope }
          : {}),
      }
      if (!shouldIncludeAccessScope(input.role, input.accessScope)) {
        delete updated.accessScope
      }
      debugInviteStore.set(existing.id, updated)
      persistDebugInviteStore(debugInviteStore)
      return { ...updated, resent: true }
    }

    const token = generateInviteToken()
    const invite = debugInsertInvite({
      id: token,
      type: 'EMAIL',
      recipient,
      token,
      role: input.role,
      workspaceId: input.workspaceId,
      workspaceName: input.workspaceName,
      workspaceSlug: input.workspaceSlug ?? null,
      status: 'PENDING',
      claimedBy: null,
      invitedBy: input.invitedBy,
      invitedByName: input.invitedByName ?? null,
      claimedAt: null,
      revokedAt: null,
      declinedAt: null,
      expiresAt,
      inviteStatusHistory: [{ status: 'PENDING', changedAt: Date.now(), reason: 'CREATED' }],
      ...(shouldIncludeAccessScope(input.role, input.accessScope) && input.accessScope
        ? { accessScope: input.accessScope }
        : {}),
    })
    return { ...invite, resent: false }
  }

  // Live Firestore flow with UPSERT semantics
  const existingQuery = query(
    collection(firestoreDb, COLLECTION),
    where('workspaceId', '==', input.workspaceId),
    where('type', '==', 'EMAIL'),
    where('recipient', '==', recipient),
  )
  const existingSnap = await getDocs(existingQuery)
  const existingDoc = existingSnap.docs.find((d) => d.data().status !== 'CLAIMED')

  if (existingDoc) {
    const existingData = mapInvite(existingDoc)
    const history = Array.isArray(existingData.inviteStatusHistory) ? [...existingData.inviteStatusHistory] : []
    history.push({ status: 'PENDING', changedAt: Date.now(), reason: 'RESENT' })
    const updatePayload: Record<string, unknown> = {
      role: input.role,
      roleRank: input.role ? (input.role === 'ADMIN' ? 4 : input.role === 'EDITOR' ? 3 : 2) : 2,
      workspaceName: input.workspaceName,
      workspaceSlug: input.workspaceSlug ?? null,
      invitedBy: input.invitedBy,
      invitedByName: input.invitedByName ?? null,
      status: 'PENDING',
      createdAt: serverTimestamp(),
      expiresAt,
      claimedAt: null,
      revokedAt: null,
      declinedAt: null,
      inviteStatusHistory: history,
    }
    if (shouldIncludeAccessScope(input.role, input.accessScope) && input.accessScope) {
      updatePayload.accessScope = input.accessScope
    }
    await updateDoc(existingDoc.ref, updatePayload)
    const snap = await getDoc(existingDoc.ref)
    const mapped = mapInvite(snap as QueryDocumentSnapshot)
    return { ...mapped, resent: true }
  }

  const token = generateInviteToken()
  const docPayload: Record<string, unknown> = {
    type: 'EMAIL',
    recipient,
    token,
    role: input.role,
    roleRank: input.role ? (input.role === 'ADMIN' ? 4 : input.role === 'EDITOR' ? 3 : 2) : 2,
    workspaceId: input.workspaceId,
    workspaceName: input.workspaceName,
    workspaceSlug: input.workspaceSlug ?? null,
    status: 'PENDING',
    claimedBy: null,
    invitedBy: input.invitedBy,
    invitedByName: input.invitedByName ?? null,
    createdAt: serverTimestamp(),
    claimedAt: null,
    revokedAt: null,
    declinedAt: null,
    expiresAt,
    inviteStatusHistory: [{ status: 'PENDING', changedAt: Date.now(), reason: 'CREATED' }],
  }
  if (shouldIncludeAccessScope(input.role, input.accessScope) && input.accessScope) {
    docPayload.accessScope = input.accessScope
  }
  await setDoc(inviteRef(firestoreDb, token), docPayload)
  const snap = await getDoc(inviteRef(firestoreDb, token))
  const mapped = mapInvite(snap as QueryDocumentSnapshot)
  return { ...mapped, resent: false }
}

export const createPhoneInvite = async (
  input: CreateInviteInput & { phoneE164: string },
): Promise<PendingInvite & { resent?: boolean }> => {
  const recipient = input.phoneE164.trim()
  if (!/^\+\d{6,15}$/.test(recipient)) throw new Error('Phone must be E.164 (e.g. +15551234567).')
  const expiresAt = input.expiresAtMs ?? (Date.now() + INVITE_TTL_MS)

  if (isNoSignInPreviewActive()) {
    const existing = Array.from(debugInviteStore.values()).find(
      (entry) => entry.workspaceId === input.workspaceId && entry.type === 'PHONE' && entry.recipient === recipient && entry.status !== 'CLAIMED',
    )
    if (existing) {
      const history = Array.isArray(existing.inviteStatusHistory) ? [...existing.inviteStatusHistory] : []
      history.push({ status: 'PENDING', changedAt: Date.now(), reason: 'RESENT' })
      const updated: PendingInvite = {
        ...existing,
        role: input.role,
        workspaceName: input.workspaceName,
        workspaceSlug: input.workspaceSlug ?? null,
        invitedBy: input.invitedBy,
        invitedByName: input.invitedByName ?? null,
        status: 'PENDING',
        createdAt: debugTimestamp(),
        expiresAt,
        claimedAt: null,
        revokedAt: null,
        declinedAt: null,
        inviteStatusHistory: history,
        ...(shouldIncludeAccessScope(input.role, input.accessScope) && input.accessScope
          ? { accessScope: input.accessScope }
          : {}),
      }
      if (!shouldIncludeAccessScope(input.role, input.accessScope)) {
        delete updated.accessScope
      }
      debugInviteStore.set(existing.id, updated)
      persistDebugInviteStore(debugInviteStore)
      return { ...updated, resent: true }
    }

    const token = generateInviteToken()
    const invite = debugInsertInvite({
      id: token,
      type: 'PHONE',
      recipient,
      token,
      role: input.role,
      workspaceId: input.workspaceId,
      workspaceName: input.workspaceName,
      workspaceSlug: input.workspaceSlug ?? null,
      status: 'PENDING',
      claimedBy: null,
      invitedBy: input.invitedBy,
      invitedByName: input.invitedByName ?? null,
      claimedAt: null,
      revokedAt: null,
      declinedAt: null,
      expiresAt,
      inviteStatusHistory: [{ status: 'PENDING', changedAt: Date.now(), reason: 'CREATED' }],
      ...(shouldIncludeAccessScope(input.role, input.accessScope) && input.accessScope
        ? { accessScope: input.accessScope }
        : {}),
    })
    return { ...invite, resent: false }
  }

  const existingQuery = query(
    collection(firestoreDb, COLLECTION),
    where('workspaceId', '==', input.workspaceId),
    where('type', '==', 'PHONE'),
    where('recipient', '==', recipient),
  )
  const existingSnap = await getDocs(existingQuery)
  const existingDoc = existingSnap.docs.find((d) => d.data().status !== 'CLAIMED')

  if (existingDoc) {
    const existingData = mapInvite(existingDoc)
    const history = Array.isArray(existingData.inviteStatusHistory) ? [...existingData.inviteStatusHistory] : []
    history.push({ status: 'PENDING', changedAt: Date.now(), reason: 'RESENT' })
    const updatePayload: Record<string, unknown> = {
      role: input.role,
      roleRank: input.role ? (input.role === 'ADMIN' ? 4 : input.role === 'EDITOR' ? 3 : 2) : 2,
      workspaceName: input.workspaceName,
      workspaceSlug: input.workspaceSlug ?? null,
      invitedBy: input.invitedBy,
      invitedByName: input.invitedByName ?? null,
      status: 'PENDING',
      createdAt: serverTimestamp(),
      expiresAt,
      claimedAt: null,
      revokedAt: null,
      declinedAt: null,
      inviteStatusHistory: history,
    }
    if (shouldIncludeAccessScope(input.role, input.accessScope) && input.accessScope) {
      updatePayload.accessScope = input.accessScope
    }
    await updateDoc(existingDoc.ref, updatePayload)
    const snap = await getDoc(existingDoc.ref)
    const mapped = mapInvite(snap as QueryDocumentSnapshot)
    return { ...mapped, resent: true }
  }

  const token = generateInviteToken()
  const docPayload: Record<string, unknown> = {
    type: 'PHONE',
    recipient,
    token,
    role: input.role,
    roleRank: input.role ? (input.role === 'ADMIN' ? 4 : input.role === 'EDITOR' ? 3 : 2) : 2,
    workspaceId: input.workspaceId,
    workspaceName: input.workspaceName,
    workspaceSlug: input.workspaceSlug ?? null,
    status: 'PENDING',
    claimedBy: null,
    invitedBy: input.invitedBy,
    invitedByName: input.invitedByName ?? null,
    createdAt: serverTimestamp(),
    claimedAt: null,
    revokedAt: null,
    declinedAt: null,
    expiresAt,
    inviteStatusHistory: [{ status: 'PENDING', changedAt: Date.now(), reason: 'CREATED' }],
  }
  if (shouldIncludeAccessScope(input.role, input.accessScope) && input.accessScope) {
    docPayload.accessScope = input.accessScope
  }
  await setDoc(inviteRef(firestoreDb, token), docPayload)
  const snap = await getDoc(inviteRef(firestoreDb, token))
  const mapped = mapInvite(snap as QueryDocumentSnapshot)
  return { ...mapped, resent: false }
}

export const listPendingInvitesForWorkspace = async (workspaceId: string): Promise<PendingInvite[]> => {
  if (isNoSignInPreviewActive()) return debugListForWorkspace(workspaceId)
  const q = query(
    collection(firestoreDb, COLLECTION),
    where('workspaceId', '==', workspaceId),
    where('status', '==', 'PENDING'),
  )
  const snap = await getDocs(q)
  return snap.docs.map(mapInvite)
}

export const listPendingInvitesForEmail = async (email: string): Promise<PendingInvite[]> => {
  const normalized = normalizeEmail(email)
  if (!normalized) return []
  const q = query(
    collection(firestoreDb, COLLECTION),
    where('type', '==', 'EMAIL'),
    where('recipient', '==', normalized),
    where('status', '==', 'PENDING'),
  )
  const snap = await getDocs(q)
  return snap.docs.map(mapInvite)
}

export type { WebPendingInvite, WebPendingInviteRole }
export { isWebPendingInvite }
export type HelperPendingInvite = PendingInvite & { role: 'HELPER' }

/**
 * Web inbox read: strictly queries and filters member invites addressed to the user.
 * Helper invites are never returned for web surfaces.
 */
export const listMemberInvitesForUser = async (
  userOrEmail: { email?: string | null } | string,
  db: Firestore = firestoreDb,
): Promise<WebPendingInvite[]> => {
  const email = typeof userOrEmail === 'string' ? userOrEmail : userOrEmail.email
  const normalized = normalizeEmail(email ?? '')
  if (!normalized) return []
  if (isNoSignInPreviewActive()) {
    return debugListForRecipient('EMAIL', normalized).filter(
      (invite): invite is WebPendingInvite =>
        invite.status === 'PENDING' && isWebPendingInvite(invite) && !isInviteExpired(invite),
    )
  }
  const q = query(
    collection(db, COLLECTION),
    where('type', '==', 'EMAIL'),
    where('recipient', '==', normalized),
    where('status', '==', 'PENDING'),
  )
  const snap = await getDocs(q)
  return snap.docs
    .map(mapInvite)
    .filter((invite): invite is WebPendingInvite => {
      if (invite.role === 'HELPER') {
        if (import.meta.env.DEV && import.meta.env.MODE !== 'test') {
          throw new Error(`[listMemberInvitesForUser] HELPER invite reached web inbox: ${invite.id}`)
        }
        console.warn('[listMemberInvitesForUser] Filtered out non-MEMBER invite on web surface:', invite.id, invite.role)
        return false
      }
      return isWebPendingInvite(invite) && !isInviteExpired(invite)
    })
}

/**
 * Web inbox live subscription: strictly subscribes to and filters member invites
 * addressed to the user. Helper invites are never returned for web surfaces.
 * Automatically schedules a timer for the nearest upcoming expiration and rechecks
 * upon page return (visibilitychange), so invites expire without extra Firestore writes.
 */
export const subscribeMemberInvitesForUser = (
  userOrEmail: { email?: string | null } | string,
  onNext: (invites: WebPendingInvite[]) => void,
  onError?: (error: Error) => void,
  db: Firestore = firestoreDb,
): Unsubscribe => {
  const email = typeof userOrEmail === 'string' ? userOrEmail : userOrEmail.email
  const normalized = normalizeEmail(email ?? '')
  if (!normalized) {
    onNext([])
    return () => {}
  }
  if (isNoSignInPreviewActive()) {
    const list = debugListForRecipient('EMAIL', normalized).filter(
      (invite): invite is WebPendingInvite =>
        invite.status === 'PENDING' && isWebPendingInvite(invite) && !isInviteExpired(invite),
    )
    onNext(list)
    return () => {}
  }

  let timerId: ReturnType<typeof setTimeout> | null = null
  let latestRawInvites: PendingInvite[] = []
  let isCleanedUp = false
  let hasReceivedFirstSnapshot = false
  let isListenerHealthy = true

  const cancelExpiryTimer = () => {
    if (timerId !== null) {
      clearTimeout(timerId)
      timerId = null
    }
  }

  const processAndEmit = (rawInvites: PendingInvite[]) => {
    if (isCleanedUp || !isListenerHealthy || !hasReceivedFirstSnapshot) return
    cancelExpiryTimer()

    const byId = new Map<string, WebPendingInvite>()
    for (const invite of rawInvites) {
      if (invite.role === 'HELPER') {
        if (import.meta.env.DEV && import.meta.env.MODE !== 'test') {
          throw new Error(`[subscribeMemberInvitesForUser] Non-MEMBER invite reached web inbox: ${invite.id} (${invite.role})`)
        }
        console.warn('[subscribeMemberInvitesForUser] Filtered out non-MEMBER invite on web surface:', invite.id, invite.role)
        continue
      }
      if (!isWebPendingInvite(invite)) continue
      if (isInviteExpired(invite)) continue
      if (invite.status === 'PENDING') {
        byId.set(invite.id, invite)
      }
    }

    const sorted = Array.from(byId.values()).sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() ?? 0
      const bTime = b.createdAt?.toMillis?.() ?? 0
      return bTime - aTime
    })

    onNext(sorted)

    // Compute nearest future expiration to re-evaluate without network writes
    const now = Date.now()
    let nearestDelay: number | null = null
    for (const invite of sorted) {
      if (typeof invite.expiresAt === 'number' && invite.expiresAt > now) {
        const delay = invite.expiresAt - now
        if (nearestDelay === null || delay < nearestDelay) {
          nearestDelay = delay
        }
      }
    }

    if (nearestDelay !== null && !isCleanedUp && isListenerHealthy) {
      const safeDelay = Math.min(nearestDelay + 50, 2 ** 31 - 1)
      timerId = setTimeout(() => {
        timerId = null
        if (!isCleanedUp && isListenerHealthy && hasReceivedFirstSnapshot) {
          processAndEmit(latestRawInvites)
        }
      }, safeDelay)
    }
  }

  const handleVisibilityChange = () => {
    if (isCleanedUp || !isListenerHealthy || !hasReceivedFirstSnapshot) return
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      processAndEmit(latestRawInvites)
    }
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', handleVisibilityChange)
  }

  const q = query(
    collection(db, COLLECTION),
    where('type', '==', 'EMAIL'),
    where('recipient', '==', normalized),
    where('status', '==', 'PENDING'),
  )

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      if (isCleanedUp) return
      hasReceivedFirstSnapshot = true
      isListenerHealthy = true
      latestRawInvites = snapshot.docs.map(mapInvite)
      processAndEmit(latestRawInvites)
    },
    (err) => {
      if (isCleanedUp) return
      isListenerHealthy = false
      cancelExpiryTimer()
      console.warn('[Firestore listener failed] subscribeMemberInvitesForUser', err)
      onError?.(err instanceof Error ? err : new Error(String(err)))
    },
  )

  return () => {
    isCleanedUp = true
    isListenerHealthy = false
    cancelExpiryTimer()
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
    unsubscribe()
  }
}

/**
 * Helper app read: strictly queries and filters HELPER invites addressed to the phone.
 * Member invites are never returned for helper surfaces.
 */
export const listHelperInvitesForPhone = async (
  phoneE164: string,
  db: Firestore = firestoreDb,
): Promise<HelperPendingInvite[]> => {
  const normalized = phoneE164.trim()
  if (!normalized) return []
  if (isNoSignInPreviewActive()) {
    return debugListForRecipient('PHONE', normalized).filter(
      (invite): invite is HelperPendingInvite => invite.status === 'PENDING' && invite.role === 'HELPER',
    )
  }
  const q = query(
    collection(db, COLLECTION),
    where('type', '==', 'PHONE'),
    where('recipient', '==', normalized),
    where('status', '==', 'PENDING'),
    where('role', '==', 'HELPER'),
  )
  const snap = await getDocs(q)
  return snap.docs
    .map(mapInvite)
    .filter((invite): invite is HelperPendingInvite => invite.role === 'HELPER')
}

export const listPendingInvitesForPhone = async (phoneE164: string): Promise<PendingInvite[]> => {
  const normalized = phoneE164.trim()
  if (!normalized) return []
  const q = query(
    collection(firestoreDb, COLLECTION),
    where('type', '==', 'PHONE'),
    where('recipient', '==', normalized),
    where('status', '==', 'PENDING'),
  )
  const snap = await getDocs(q)
  return snap.docs.map(mapInvite)
}

export const listIncomingInvitesForEmail = async (email: string): Promise<PendingInvite[]> => {
  const normalized = normalizeEmail(email)
  if (!normalized) return []
  if (isNoSignInPreviewActive()) return debugListForRecipient('EMAIL', normalized).filter((inv) => !isInviteExpired(inv))
  const rows = await Promise.all(INCOMING_INVITE_STATUSES.map(async (status) => {
    const q = query(
      collection(firestoreDb, COLLECTION),
      where('type', '==', 'EMAIL'),
      where('recipient', '==', normalized),
      where('status', '==', status),
    )
    const snap = await getDocs(q)
    return snap.docs.map(mapInvite)
  }))
  return rows.flat().filter((inv) => !isInviteExpired(inv))
}

export const listIncomingInvitesForPhone = async (phoneE164: string): Promise<PendingInvite[]> => {
  const normalized = phoneE164.trim()
  if (!normalized) return []
  if (isNoSignInPreviewActive()) return debugListForRecipient('PHONE', normalized).filter((inv) => !isInviteExpired(inv))
  const rows = await Promise.all(INCOMING_INVITE_STATUSES.map(async (status) => {
    const q = query(
      collection(firestoreDb, COLLECTION),
      where('type', '==', 'PHONE'),
      where('recipient', '==', normalized),
      where('status', '==', status),
    )
    const snap = await getDocs(q)
    return snap.docs.map(mapInvite)
  }))
  return rows.flat().filter((inv) => !isInviteExpired(inv))
}

export const fetchInviteByToken = async (token: string): Promise<PendingInvite | null> => {
  if (isNoSignInPreviewActive()) return debugInviteStore.get(token) ?? null
  const snap = await getDoc(inviteRef(firestoreDb, token))
  if (!snap.exists()) return null
  return mapInvite(snap as QueryDocumentSnapshot)
}

export const revokeInvite = async (inviteId: string, revokedBy: string): Promise<void> => {
  if (isNoSignInPreviewActive()) {
    const existing = debugInviteStore.get(inviteId)
    if (!existing) return
    if (existing.status === 'CLAIMED') throw new Error('Invite is already claimed.')
    debugInviteStore.set(inviteId, { ...existing, status: 'REVOKED', revokedAt: debugTimestamp() })
    persistDebugInviteStore(debugInviteStore)
    return
  }
  const ref = inviteRef(firestoreDb, inviteId)
  await runTransaction(firestoreDb, async (tx) => {
    const snap = await tx.get(ref)
    if (!snap.exists()) return
    const data = snap.data() as Record<string, unknown>
    if (data.status === 'CLAIMED') throw new Error('Invite is already claimed.')
    tx.update(ref, {
      status: 'REVOKED' satisfies PendingInviteStatus,
      revokedAt: serverTimestamp(),
      revokedBy,
    })
  })
}

export const deleteInvite = async (inviteId: string): Promise<void> => {
  await deleteDoc(inviteRef(firestoreDb, inviteId))
}

export interface ClaimInviteResult {
  workspaceId: string
  workspaceName: string
  role: WorkspaceRole
  inviteId: string
  alreadyMember: boolean
}

export interface ClaimingUser {
  uid: string
  email: string | null
  phoneE164: string | null
  displayName: string | null
}

const claimUnifiedInviteCallable = httpsCallable<
  { inviteId?: string; token?: string; email?: string | null; phoneNumber?: string | null; displayName?: string | null },
  ClaimInviteResult
>(firebaseFunctions, 'claimUnifiedInvite')

const createHelperPhoneInviteCallable = httpsCallable<
  { workspaceId: string; phoneE164: string },
  { inviteId: string; notifiedUserId: string | null }
>(firebaseFunctions, 'createHelperPhoneInvite')

const declineUnifiedInviteCallable = httpsCallable<
  { inviteId: string; email?: string | null; phoneNumber?: string | null; displayName?: string | null },
  { inviteId: string; status: 'DECLINED' }
>(firebaseFunctions, 'declineUnifiedInvite')

export class WorkspaceCapReachedError extends Error {
  readonly code = 'CAP_REACHED' as const
  constructor(message = "CAP_REACHED: You've reached the 20-workspace limit. Leave one before accepting another.") {
    super(message)
    this.name = 'WorkspaceCapReachedError'
  }
}

/**
 * Count active non-owned workspaces (MEMBER + HELPER) for a user.
 * Merges top-level workspace_members (by canonical userId and uid, or phone for phone helpers)
 * with the mirrored users/{uid}/workspaceMemberships subcollection to match WorkspaceContext's
 * membership universe.
 */
export const countUserActiveNonOwnedWorkspaces = async (
  user: string | Pick<ClaimingUser, 'uid' | 'email' | 'phoneE164'>,
  db: Firestore = firestoreDb,
): Promise<number> => {
  const identity = typeof user === 'string'
    ? { uid: user, email: user, phoneE164: null }
    : user
  const uid = identity?.uid
  if (!uid) return 0

  type MembershipSnapshotLike = {
    docs: Array<{
      id: string
      data: () => Record<string, unknown>
    }>
  }

  const queries: Promise<MembershipSnapshotLike>[] = []
  if (identity.email) {
    queries.push(getDocs(query(collection(db, 'workspace_members'), where('uid', '==', uid))))
    queries.push(getDocs(query(collection(db, 'workspace_members'), where('userId', '==', uid))))
  }
  if (identity.phoneE164) {
    queries.push(getDocs(query(collection(db, 'workspace_members'), where('phone', '==', identity.phoneE164))))
  }
  queries.push(getDocs(collection(db, 'users', uid, 'workspaceMemberships')).catch(() => ({ docs: [] })))

  const snapshots = await Promise.all(queries)
  const counted = new Set<string>()
  for (const snap of snapshots) {
    for (const docSnap of snap?.docs ?? []) {
      const data = docSnap.data()
      const status = data?.['status'] ?? ''
      const role = data?.['role'] ?? ''
      if (status === 'ACTIVE' && role !== 'OWNER') {
        const wsId = typeof data?.['workspaceId'] === 'string' ? data['workspaceId'] : ''
        counted.add(wsId || docSnap.id)
      }
    }
  }
  return counted.size
}

/**
 * Claim a pending invite. The actual write is done server-side by the
 * `claimUnifiedInvite` cloud function — that lets the membership doc, the
 * invite-CLAIMED flip, and the MEMBER_JOIN audit row land in a single
 * admin-SDK transaction (rules can't satisfy this client-side because the
 * audit-row rule checks for a membership that doesn't exist yet at claim
 * time). Idempotent: re-claiming a CLAIMED invite for the same uid is a no-op.
 */
export const claimPendingInvite = async (
  inviteId: string,
  user: ClaimingUser,
  db: Firestore = firestoreDb,
): Promise<ClaimInviteResult> => {
  if (!user.uid) throw new Error('Missing uid for invite claim.')

  const activeCount = await countUserActiveNonOwnedWorkspaces(user, db)
  if (activeCount >= getMaxNonOwnedWorkspaces()) {
    throw new WorkspaceCapReachedError()
  }

  if (isNoSignInPreviewActive()) {
    const existing = debugInviteStore.get(inviteId)
    if (!existing) throw new Error('This invite no longer exists.')
    if (existing.status === 'REVOKED') throw new Error('This invite has been revoked.')
    const alreadyMember = existing.status === 'CLAIMED' && existing.claimedBy === user.uid
    debugInviteStore.set(inviteId, {
      ...existing,
      status: 'CLAIMED',
      claimedBy: user.uid,
      claimedAt: debugTimestamp(),
    })
    persistDebugInviteStore(debugInviteStore)
    const claimedRole: WorkspaceRole = isWorkspaceRole(existing.role)
      ? existing.role
      : 'VIEWER'
    return {
      workspaceId: existing.workspaceId,
      workspaceName: existing.workspaceName,
      role: claimedRole,
      inviteId,
      alreadyMember,
    }
  }
  const result = await claimUnifiedInviteCallable({
    inviteId,
    email: user.email,
    phoneNumber: user.phoneE164,
    displayName: user['displayName'],
  })
  return result.data
}

export const createHelperPhoneInvite = async (
  input: { workspaceId: string; phoneE164: string } & Partial<CreateInviteInput>,
): Promise<PendingInvite> => {
  if (isNoSignInPreviewActive()) {
    return createPhoneInvite({
      workspaceName: '',
      invitedBy: '',
      ...input,
      role: 'HELPER',
      phoneE164: input.phoneE164,
    })
  }
  const result = await createHelperPhoneInviteCallable({
    workspaceId: input.workspaceId,
    phoneE164: input.phoneE164,
  })
  const invite = await fetchInviteByToken(result.data.inviteId)
  if (!invite) throw new Error('Helper phone invite was created, but could not be loaded.')
  return invite
}

export const declinePendingInvite = async (
  inviteId: string,
  user: ClaimingUser,
): Promise<{ inviteId: string; status: 'DECLINED' }> => {
  if (!user.uid) throw new Error('Missing uid for invite decline.')
  if (isNoSignInPreviewActive()) {
    const existing = debugInviteStore.get(inviteId)
    if (!existing) throw new Error('This invite no longer exists.')
    debugInviteStore.set(inviteId, {
      ...existing,
      status: 'DECLINED',
      claimedBy: null,
      declinedAt: debugTimestamp(),
    })
    persistDebugInviteStore(debugInviteStore)
    return { inviteId, status: 'DECLINED' }
  }
  const result = await declineUnifiedInviteCallable({
    inviteId,
    email: user.email,
    phoneNumber: user.phoneE164,
    displayName: user.displayName,
  })
  return result.data
}

const PENDING_LINK_TOKEN_KEY = 'timelining.pendingInviteToken'
const PENDING_LINK_MAX_AGE_MS = 30 * 60 * 1000 // 30 minutes

interface StashedLinkToken {
  token: string
  savedAt: number
}

export const clearPendingLinkToken = (): void => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(PENDING_LINK_TOKEN_KEY)
  } catch {
    // localStorage unavailable — silent fallback
  }
}

export const stashPendingLinkToken = (token: string): void => {
  if (typeof window === 'undefined') return
  try {
    const payload: StashedLinkToken = {
      token,
      savedAt: Date.now(),
    }
    window.localStorage.setItem(PENDING_LINK_TOKEN_KEY, JSON.stringify(payload))
  } catch {
    // localStorage unavailable — silent fallback
  }
}

const readAndValidatePendingLinkToken = (): string | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(PENDING_LINK_TOKEN_KEY)
    if (!raw) return null
    let parsed: unknown
    try {
      parsed = JSON.parse(raw)
    } catch {
      window.localStorage.removeItem(PENDING_LINK_TOKEN_KEY)
      return null
    }

    if (
      !parsed ||
      typeof parsed !== 'object' ||
      typeof (parsed as Record<string, unknown>).token !== 'string' ||
      typeof (parsed as Record<string, unknown>).savedAt !== 'number'
    ) {
      window.localStorage.removeItem(PENDING_LINK_TOKEN_KEY)
      return null
    }

    const payload = parsed as { token: string; savedAt: number }
    const age = Date.now() - payload.savedAt
    if (age < 0 || age > PENDING_LINK_MAX_AGE_MS) {
      window.localStorage.removeItem(PENDING_LINK_TOKEN_KEY)
      return null
    }

    return payload.token
  } catch {
    return null
  }
}

export const consumePendingLinkToken = (): string | null => {
  if (typeof window === 'undefined') return null
  try {
    const token = readAndValidatePendingLinkToken()
    window.localStorage.removeItem(PENDING_LINK_TOKEN_KEY)
    return token
  } catch {
    return null
  }
}

export const peekPendingLinkToken = (): string | null => {
  return readAndValidatePendingLinkToken()
}

export const buildJoinUrl = (token: string): { web: string; deepLink: string } => {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://timelining.app'
  return {
    web: `${origin}/join/${token}`,
    deepLink: `timelining://join/${token}`,
  }
}

/**
 * Parse a join token from a URL or deep link. Returns null if the input
 * doesn't match any known join scheme. Accepts:
 *   - https://timelining.app/join/{token}
 *   - https://<any-host>/join/{token}
 *   - timelining://join/{token}
 *   - /join/{token}
 */
export const parseJoinToken = (input: string): string | null => {
  if (!input) return null
  const trimmed = input.trim()
  const deepMatch = trimmed.match(/^timelining:\/\/join\/([A-Za-z0-9_-]+)(?:[/?#]|$)/)
  if (deepMatch) return deepMatch[1] ?? null

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    try {
      const parsed = new URL(trimmed)
      const pathMatch = parsed.pathname.match(/^\/join\/([A-Za-z0-9_-]+)\/?$/)
      return pathMatch ? pathMatch[1] ?? null : null
    } catch {
      return null
    }
  }

  if (trimmed.startsWith('/join/')) {
    try {
      const parsed = new URL(trimmed, 'https://dummy.local')
      const pathMatch = parsed.pathname.match(/^\/join\/([A-Za-z0-9_-]+)\/?$/)
      return pathMatch ? pathMatch[1] ?? null : null
    } catch {
      const pathMatch = trimmed.match(/^\/join\/([A-Za-z0-9_-]+)\/?$/)
      return pathMatch ? pathMatch[1] ?? null : null
    }
  }

  return null
}
